import React from "react";
import {Drawer, IconButton, Tooltip} from "@mui/material";
import {Close, InfoOutlined, LockOutlined} from "@mui/icons-material";
import type {ContractChild} from "../contractCoverageData";
import {COVERAGE_CURRENCY} from "../contractCoverageData";
import {formatCompactCurrency, POSITION_LABEL, positionColors} from "../contractCoverageUtils";
import {baselineValue, needFromScore, SCORE_DIMENSIONS, SCORE_MAX, VERDICT_LABEL} from "../scoringModel";

type Props = {
    open: boolean;
    onClose: () => void;
    child: ContractChild | null;
};

// Zebra variant: only the data-backed dimensions are part of the scoring
// view — reviewer-assessed dimensions are excluded, and nothing is editable.
const DATA_DIMENSIONS = SCORE_DIMENSIONS.filter(d => d.dataField);

export const ScoreViewDrawer: React.FC<Props> = ({open, onClose, child}) => {
    if (!child) return null;

    const score = child.weightedScore;
    const col = positionColors(child.position);
    const money = (v: number) => formatCompactCurrency(v, COVERAGE_CURRENCY);

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{className: 'cci-score-paper'}}
                sx={theme => ({zIndex: theme.zIndex.drawer + 2})}>
            <div className="cci-score ccz-score">
                <header className="cci-score-head">
                    <div>
                        <span className="cci-score-eyebrow">Scoring</span>
                        <h3>{child.supplier}</h3>
                        <span className="cci-score-sub">
                            {child.country && <span className="cci-country-chip cci-inline">{child.country}</span>}
                            {money(child.spend)} cumulative spend
                        </span>
                    </div>
                    <IconButton onClick={onClose} size="small"><Close/></IconButton>
                </header>

                <div className="cci-score-body">
                    <div className="cci-verdict ccz-verdict-single">
                        <div className="cci-verdict-side">
                            <span className="cci-verdict-label">Weighted score</span>
                            <span className="cci-verdict-score">{score.toFixed(2)}<small>/5</small></span>
                            <span className="cci-verdict-need">{VERDICT_LABEL[needFromScore(score)]}</span>
                            <span className="cci-position-pill" style={{backgroundColor: col.bg, color: col.fg}}>
                                {POSITION_LABEL[child.position]}
                            </span>
                        </div>
                    </div>

                    <div className="cci-dims">
                        {DATA_DIMENSIONS.map(dim => {
                            const val = baselineValue(child, dim);
                            return (
                                <div className="cci-dim" key={dim.key}>
                                    <div className="cci-dim-head">
                                        <span className="cci-dim-label">
                                            {dim.label}
                                            <Tooltip title={dim.definition} arrow placement="top">
                                                <InfoOutlined className="cci-dim-info" fontSize="inherit"/>
                                            </Tooltip>
                                        </span>
                                        <span className="cci-dim-value">{val.toFixed(1)}</span>
                                    </div>
                                    <p className="cci-dim-def">{dim.definition}</p>
                                    <div className="ccz-dim-meter" role="meter" aria-valuemin={0}
                                         aria-valuemax={SCORE_MAX} aria-valuenow={val}>
                                        <span style={{width: `${(val / SCORE_MAX) * 100}%`}}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <footer className="cci-score-foot ccz-score-foot">
                    <span className="ccz-lock-note">
                        <LockOutlined fontSize="inherit"/>
                        Scores are calculated from source data and cannot be adjusted.
                    </span>
                </footer>
            </div>
        </Drawer>
    );
};
